import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Text,
  Group,
  Grid,
  Select,
  MultiSelect,
  Paper,
  SimpleGrid,
  Skeleton,
  Alert,
  Stack,
} from '@mantine/core';
import { IconAlertCircle, IconFilter } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ReportListCard } from '../reports/ReportListCard';
import { PageHeader } from '../common/PageHeader';
import { reportsApi } from '../../api/reports';
import { AdminSubawardeeFilterSelect } from './AdminSubawardeeFilterSelect';
import { useSyncSelectedGroupFromSubawardee } from '../../hooks/useSyncSelectedGroupFromSubawardee';
import { useAdminSubawardeeUrlState } from '../../hooks/useAdminSubawardeeUrlState';
import { withAdminSubawardeeQuery } from '../../utils/adminSubawardeePaths';

const filterInputStyles = (theme: any) => {
  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
  return {
    label: { fontSize: theme.fontSizes.xs, fontWeight: 500, marginBottom: 4 },
    input: {
      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' }
    },
    searchInput: {
      '&:focus': { outline: 'none', boxShadow: 'none' }
    }
  };
};

export function AdminReportsPage() {
  const navigate = useNavigate();
  const [subawardee, setSubawardee] = useAdminSubawardeeUrlState();
  useSyncSelectedGroupFromSubawardee(subawardee);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  useEffect(() => {
    setSelectedYear(null);
    setSelectedStatuses([]);
  }, [subawardee]);

  const {
    data: reports,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-reports', subawardee],
    queryFn: () => reportsApi.getAllReportsForSubawardee(subawardee!),
    enabled: Boolean(subawardee),
    staleTime: 2 * 60 * 1000,
  });

  const filteredReports = useMemo(() => {
    return (
      reports?.filter((report) => {
        const matchesYear =
          !selectedYear || dayjs(report.dueDate).format('YYYY') === selectedYear;
        const matchesStatus =
          selectedStatuses.length === 0 || selectedStatuses.includes(report.status);
        return matchesYear && matchesStatus;
      }) || []
    ).sort((a, b) => dayjs(b.dueDate).valueOf() - dayjs(a.dueDate).valueOf());
  }, [reports, selectedYear, selectedStatuses]);

  const availableYears = Array.from(
    new Set(reports?.map((report) => dayjs(report.dueDate).format('YYYY')) || [])
  );

  const statusOptions = [
    { value: 'not_submitted', label: 'Not Submitted' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'in_review', label: 'In Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const secondaryFiltersDisabled = !subawardee || isLoading;

  const openReportInAdminViewOnly = (reportId: string): void => {
    navigate(withAdminSubawardeeQuery(`/admin/reports/${reportId}`, subawardee));
  };

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Reports"
        subtitle="View quarterly reports for a selected subawardee. Use the same flows as the main app to open or edit a report."
      />

      <Paper
        radius="md"
        p="md"
        mb="lg"
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
            padding: theme.spacing.sm,
          },
        })}
      >
        <Group spacing={6} mb="sm">
          <IconFilter size={15} style={{ opacity: 0.55 }} />
          <Text size="xs" weight={600} tt="uppercase" color="dimmed" sx={{ letterSpacing: 0.5 }}>
            Filters
          </Text>
        </Group>

        <Grid gutter="sm">
          <Grid.Col span={4} xs={12} sm={4}>
            <AdminSubawardeeFilterSelect
              value={subawardee}
              onChange={setSubawardee}
              disabled={isLoading}
              style={undefined}
              styles={filterInputStyles}
              size="sm"
            />
          </Grid.Col>
          <Grid.Col span={4} xs={12} sm={4}>
            <Select
              label="Year"
              placeholder="All years"
              value={selectedYear}
              onChange={setSelectedYear}
              clearable
              data={availableYears.map((year) => ({ value: year, label: year }))}
              disabled={secondaryFiltersDisabled}
              size="sm"
              styles={filterInputStyles}
            />
          </Grid.Col>
          <Grid.Col span={4} xs={12} sm={4}>
            <MultiSelect
              label="Status"
              placeholder="All statuses"
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              clearable
              data={statusOptions}
              disabled={secondaryFiltersDisabled}
              size="sm"
              styles={filterInputStyles}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {!subawardee ? (
        <Alert color="blue" mb="xl">
          Choose a subawardee in Filters to load reports for that organization.
        </Alert>
      ) : null}

      {error ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          role="alert"
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            },
          })}
        >
          Failed to load reports. Please try again later.
        </Alert>
      ) : null}

      {subawardee ? (
        <SimpleGrid
          cols={3}
          spacing="lg"
          breakpoints={[
            { maxWidth: 'md', cols: 2 },
            { maxWidth: 'sm', cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' },
          ]}
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            },
          })}
        >
          {isLoading ? (
            Array(6)
              .fill(0)
              .map((_, index) => (
                <Paper key={index} p="md" withBorder>
                  <Stack spacing="md">
                    <Skeleton height={24} width="70%" radius="md" />
                    <Skeleton height={20} width="40%" radius="md" />
                    <Group position="apart" mt="md" sx={(theme) => ({
                      '@media (max-width: 480px)': {
                        flexDirection: 'column',
                        gap: theme.spacing.sm,
                      },
                    })}>
                      <Skeleton height={32} width={100} radius="md" />
                      <Skeleton height={32} width={100} radius="md" />
                    </Group>
                  </Stack>
                </Paper>
              ))
          ) : error ? null : filteredReports.length > 0 ? (
            filteredReports.map((report) => (
              <ReportListCard
                key={report.id}
                report={report}
                viewOnly
                onSubmit={openReportInAdminViewOnly}
                onView={openReportInAdminViewOnly}
                onUpdate={openReportInAdminViewOnly}
              />
            ))
          ) : (
            <Text align="center" color="dimmed" style={{ gridColumn: '1 / -1' }}>
              No reports match the selected filters.
            </Text>
          )}
        </SimpleGrid>
      ) : null}
    </Container>
  );
}
